import React from "react";
import { formatTimeAgo } from "views_shared/app/javascript/utils/dateFormatters";
import ProfilePostingRate from "./ProfilePostingRate";

const BLOCKING_CHECK_TYPES = ["safety", "substance", "fitness", "completeness", "overload"];
const ADVISORY_CHECK_TYPES = ["editorial"];

const CHECK_LABELS = {
  safety: "Safety",
  substance: "Substance",
  fitness: "Fitness",
  completeness: "Completeness",
  overload: "Overload",
  editorial: "Editorial",
};

const FAILURE_LABELS = {
  missing_outlet: "Media outlet assigned",
  title_too_long: "Title length (max 90 chars)",
  has_email: "No email addresses in description",
  has_phone: "No phone numbers in description",
  has_links: "No links in content",
  has_form_links: "No form/survey links",
  has_social_links: "No social media links",
};

const ADVISORY_LABELS = {
  missing_outlet_logo: "Publication has logo",
};

function CheckRow({ checkType, result, isTerminal, stale }) {
  if (stale) {
    return (
      <div className="d-flex align-items-center gap-2 py-2">
        <i className="fa fa-spinner fa-spin text-info" />
        <span className="fw-medium">{CHECK_LABELS[checkType]}</span>
        <span className="text-muted small ms-auto">Re-evaluating...</span>
      </div>
    );
  }

  if (!result) {
    if (isTerminal) return null;
    return (
      <div className="d-flex align-items-center gap-2 py-2">
        <i className="fa fa-clock-o text-muted" />
        <span className="fw-medium">{CHECK_LABELS[checkType]}</span>
        <span className="text-muted small ms-auto">Pending...</span>
      </div>
    );
  }

  const passed = result.pass;

  return (
    <div className="d-flex align-items-start gap-2 py-2">
      {passed ? (
        <i className="fa fa-check-circle text-success mt-1" />
      ) : (
        <i className="fa fa-times-circle text-danger mt-1" />
      )}
      <div className="flex-grow-1">
        <span className="fw-medium">{CHECK_LABELS[checkType]}</span>
        {result.reason && <span className="text-muted small d-block">{result.reason}</span>}
        {checkType === "completeness" && result.failures && (
          <StructuredDetails items={result.failures} labels={FAILURE_LABELS} icon="times" color="danger" />
        )}
        {checkType === "completeness" && result.advisories && (
          <StructuredDetails items={result.advisories} labels={ADVISORY_LABELS} icon="info-circle" color="warning" />
        )}
      </div>
    </div>
  );
}

function StructuredDetails({ items, labels, icon, color }) {
  const keys = Object.keys(items).filter((k) => items[k]);
  if (keys.length === 0) return null;

  return (
    <ul className="list-unstyled mb-0 mt-1">
      {keys.map((key) => (
        <li key={key} className={`text-${color} small`}>
          <i className={`fa fa-${icon} me-1`} />
          {labels[key] || key}
        </li>
      ))}
    </ul>
  );
}

function AdvisoryCheckRow({ checkType, result }) {
  const passed = result.pass;
  return (
    <div className="d-flex align-items-start gap-2 py-2 border-top">
      {passed ? (
        <i className="fa fa-check-circle text-success mt-1" />
      ) : (
        <i className="fa fa-exclamation-triangle text-warning mt-1" />
      )}
      <div className="flex-grow-1">
        <span className="fw-medium">{CHECK_LABELS[checkType]}</span>
        <span className="badge bg-light text-muted ms-1" style={{ fontSize: "0.65rem" }}>
          advisory
        </span>
        {result.reason && <span className="text-muted small d-block">{result.reason}</span>}
      </div>
    </div>
  );
}

function ContentModResults({ results, request, recentRequests, reprocessingSince, reprocessingHash }) {
  // Authoritative content hash — determines which moderation run to display.
  // Priority: reprocessingHash (from active/recent resubmit, kept in React state)
  //         > request.current_content_hash (from backend view, survives page reload)
  //         > newest blocking result's hash (legacy fallback)
  const contentResults = results.filter((res) => res.content_hash);
  const blockingResults = contentResults.filter((res) => BLOCKING_CHECK_TYPES.includes(res.check_name));
  const latestHash = reprocessingHash || request.current_content_hash || blockingResults[0]?.content_hash;
  const latestResults = latestHash ? contentResults.filter((res) => res.content_hash === latestHash) : [];

  // Rate limit result — scoped to the authoritative hash when possible.
  // Mirrors coordinator logic: falls back to unscoped only if no hash-scoped
  // rate_limit exists (pre-nonce legacy era).
  const rateLimitResult = (() => {
    const all = results.filter((res) => res.check_name === "rate_limit");
    if (latestHash) {
      const hashScoped = all.find((res) => res.content_hash === latestHash);
      if (hashScoped) return hashScoped;
      if (all.some((res) => res.content_hash)) return null;
    }
    return all[0] || null;
  })();

  // Build a map of check_name -> result
  const resultMap = {};
  latestResults.forEach((res) => {
    if (!resultMap[res.check_name]) {
      resultMap[res.check_name] = res;
    }
  });

  // During reprocessing, mark checks as stale if they don't match the current run's hash.
  // Falls back to timestamp comparison if no hash is available.
  const isCheckStale = (checkType) => {
    if (!reprocessingSince) return false;
    const result = resultMap[checkType];
    if (!result) return true;
    if (reprocessingHash) return result.content_hash !== reprocessingHash;
    return new Date(result.created_at) < new Date(reprocessingSince);
  };

  const isRateLimitStale = (() => {
    if (!reprocessingSince) return false;
    if (!rateLimitResult) return true;
    if (reprocessingHash) return rateLimitResult.content_hash !== reprocessingHash;
    return new Date(rateLimitResult.created_at) < new Date(reprocessingSince);
  })();

  const isTerminal = !!(request.approved_at || request.ghosted_at);
  const contentPassed = isTerminal
    ? BLOCKING_CHECK_TYPES.every((ct) => !resultMap[ct] || resultMap[ct].pass)
    : BLOCKING_CHECK_TYPES.every((ct) => resultMap[ct]?.pass);
  const allPassed = contentPassed && rateLimitResult?.pass !== false;
  const isManualReview = request.needs_manual_approval_at && !request.approved_at && !request.ghosted_at;
  const ranAt = latestResults[0]?.created_at;

  return (
    <div className="card h-100">
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Content Moderation</h6>
        {ranAt && <span className="text-muted small">Ran {formatTimeAgo(ranAt)}</span>}
      </div>
      <div className="card-body">
        {latestResults.length === 0 && !rateLimitResult && !reprocessingSince ? (
          <p className="text-muted small">No content moderation results available.</p>
        ) : (
          <>
            {BLOCKING_CHECK_TYPES.map((checkType) => (
              <CheckRow
                key={checkType}
                checkType={checkType}
                result={resultMap[checkType]}
                isTerminal={isTerminal}
                stale={isCheckStale(checkType)}
              />
            ))}

            {ADVISORY_CHECK_TYPES.map(
              (checkType) =>
                resultMap[checkType] && (
                  <AdvisoryCheckRow key={checkType} checkType={checkType} result={resultMap[checkType]} />
                ),
            )}

            <ProfilePostingRate
              recentRequests={recentRequests}
              rateLimitResult={rateLimitResult}
              stale={!!isRateLimitStale}
            />

            {allPassed && isManualReview && (
              <p className="text-muted small fst-italic mt-2 mb-0">
                All checks passed. Held for manual review due to {request.automation_reason || "configuration"}, not
                content.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ContentModResults;
